import { useCallback, useEffect, useState } from 'react'
import { getApiClient } from '../../../stores'
import type { ClientOutputs } from '@botpress/client'
import {
  Button,
  Card,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  showErrorToast,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@bpinternal/ui-kit-next'
import { cn } from '../../../utils'
import { Badge, JSONTree } from '../../../elements'
import type { Color } from '../../../types'
import { formatDate } from '@bpinternal/shared'

type Props = { className?: string; botId: string; workspaceId: string }

// workflows
// status -> pending
// sub workflows hierarcy (tree)
// input with getstate from string
type Workflow = ClientOutputs['listWorkflows']['workflows'][number]

export const WorkflowTable = ({ className, botId, workspaceId, ...props }: Props) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [loading, setLoading] = useState(false)

  const refetchWorkflows = useCallback(async () => {
    setLoading(true)
    try {
      const client = getApiClient({ botId, workspaceId })

      const { workflows } = await client.listWorkflows({})
      setWorkflows(workflows)
    } catch (error) {
      showErrorToast('Failed to fetch workflows')
    } finally {
      setLoading(false)
    }
  }, [botId, workspaceId])

  useEffect(() => {
    refetchWorkflows()
  }, [refetchWorkflows])

  return (
    <>
      <Button className="mb-3" onClick={() => refetchWorkflows()} disabled={loading}>
        Refresh
      </Button>
      <Card {...props} className={cn(className, 'p-0')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Updated At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Failure Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workflows.map((workflow) => (
              <WorkflowRow key={workflow.id} workflow={workflow} />
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  )
}

const WorkflowRow = ({ workflow }: { workflow: Workflow }) => {
  const { name, updatedAt, status, failureReason } = workflow
  const [drawerOpen, setDrawerOpen] = useState(false)

  const statusToColorMap: Record<Workflow['status'], Color> = {
    pending: 'blue',
    cancelled: 'gray',
    in_progress: 'blue',
    listening: 'blue',
    paused: 'yellow',
    timedout: 'red',
    completed: 'green',
    failed: 'red',
  }

  return (
    <>
      <TableRow className="hover:bg-accent" onClick={() => setDrawerOpen(true)}>
        <TableCell className="text-gray-12">{name}</TableCell>
        <TableCell className="text-sm text-gray-11">
          {formatDate(updatedAt, { includeTime: true, format: 'medium' })}
        </TableCell>
        <TableCell>
          <Badge color={statusToColorMap[status]}>{status}</Badge>
        </TableCell>
        <TableCell>{failureReason}</TableCell>
      </TableRow>
      <Sheet key={workflow.id} open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Workflow Details</SheetTitle>
            <JSONTree data={workflow} />
          </SheetHeader>
        </SheetContent>
      </Sheet>
    </>
  )
}
